import { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { KIND, SIZE } from 'baseui/button';
import { Tab } from 'baseui/tabs';
import { FILL } from 'baseui/tabs-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Layout, { LayoutContent, LayoutHeader } from 'components/Common/Layout';
import CreateBroadcastBtn from 'components/shared/CreateBroadcastBtn/CreateBroadcastBtn';
import CreateClampaignBtn from 'components/shared/CreateCampaignBtn/CreateCampaignBtn';
import CreateClickToWAAdBtn from 'components/shared/CreateClickToWAAdBtn/CreateClickToWAAdBtn';
import ENGTButton from 'components/UI/ENGTButton/ENGTButton';
import ENGTHeader from 'components/UI/ENGTHeader/ENGTHeader';
import ENGTTabs, { tabsDefaultOverride } from 'components/UI/ENGTTabs/ENGTTabs';
import PageHeader, { PageSubHeading } from 'components/UI/PageHeader/PageHeader';

import { renderWhatsappTemplateNote } from 'shared/consts/broadcast/constants';
import { writeToLs } from 'shared/consts/localStorageConsts';
import { IObjProps } from 'shared/consts/types';
import { CHANNELS, EXECUTION_TAB, LOCAL_STORAGE_VALUES, MARKETING_CAMPAIGNS_ENTITLEMENT_VALUES } from 'shared/enum';
import useAccountStatusHook from 'shared/hooks/feature/useAccountStatusHook';
import useResponsiveSize from 'shared/hooks/useResponsiveSize';

import {
	isAcceleratorEcomSelector,
	isAiAgentsEnabledSelector,
	isCTWAEntitlementEnabledSelector,
	isSandboxConfiguredSelector,
	isSandboxEntitlementEnabled,
	isTelephonyConfiguredSelector,
	isWhatsappAcceleratorEnabled,
	isWhatsAppCloudConfiguredAsProvider,
	isWhatsAppConfiguredSelector,
	marketingCampaignsAnalyticsSelector,
} from 'store/App/User/selectors';
import { configuredChannelsForBot } from 'store/Bot/selectors';
import { isEngatiBrandSelector } from 'store/Users/selectors';

import { ThreeColumnRouteLinks } from 'router/links/ThreeColumnRouteLinks';

import CreateVoiceCampaignBtn from 'pages/BroadcastRevamp/VoiceCampaigns/CreateVoiceCampaign/components/CreateVoiceCampaignBtn';
import MaxPricingEnrollment from 'pages/WhatsApp/Numbers/MaxPricingEnrollment';

import BroadcastHistory from '../BroadcastHistory/BroadcastHistory';
import useBroadcastListHook from '../BroadcastHistory/hooks/useBroadcastListHook';
import CampaignsV2 from '../Campaigns/CampaignsV2';
import AdDetailsTab from '../ClickToWhatsappAds/components/AdDetailsTab';
import VoiceCampaigns from '../VoiceCampaigns/VoiceCampaigns';

const Execution = ({ tab }: { tab: EXECUTION_TAB }) => {
	const { t } = useTranslation(['common', 'pages']);
	const { isSmallDesktopScreen } = useResponsiveSize();
	const [css, theme]: [any, any] = useStyletron();

	const [fetchAccountStatus] = useAccountStatusHook({
		publishedPath: 'published',
	});

	const broadcastHelpers = useBroadcastListHook();

	const isCTWAEntitlementEnabled = useSelector(isCTWAEntitlementEnabledSelector);
	const isWhatsAppConfigured = useSelector(isWhatsAppConfiguredSelector);
	const isWhatsAppCloudConfigured = useSelector(isWhatsAppCloudConfiguredAsProvider);
	const isTelephonyConfigured = useSelector(isTelephonyConfiguredSelector);
	const configuredChannels = useSelector(configuredChannelsForBot);
	const isSandboxEnabled = useSelector(isSandboxEntitlementEnabled);
	const isAcceleratorEcom = useSelector(isAcceleratorEcomSelector);
	const isWhatsappSandboxConfigured = useSelector(isSandboxConfiguredSelector);
	const isEngatiBrand = useSelector(isEngatiBrandSelector);
	const isWAAcceleratorEnabled = useSelector(isWhatsappAcceleratorEnabled);
	const marketingCampaignsAnalyticsType = useSelector(marketingCampaignsAnalyticsSelector);
	const isAiAgentsEnabled = useSelector(isAiAgentsEnabledSelector);
	const shouldShowVoiceCampaignsTab = isAiAgentsEnabled;
	const hasVoiceCampaignChannelConfigured = isWhatsAppConfigured || isTelephonyConfigured;

	const [campaignModal, toggleCampaignModal] = useState<boolean>(false);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const [hasDialog360Provider, setHasDialog360Provider] = useState<boolean>(false);
	const [isMaxPricingEnabled, setMaxPricingEnabled] = useState<boolean | undefined>(undefined);
	const [selectedCampaign, setSelectedCampaign] = useState<IObjProps>({});
	const navigate = useNavigate();

	useEffect(() => {
		broadcastHelpers.getWhatsappConfigration(setHasDialog360Provider);
	}, []);

	useEffect(() => {
		if (!shouldShowVoiceCampaignsTab && tab === EXECUTION_TAB.VOICE_CAMPAIGNS) {
			navigate(`${ThreeColumnRouteLinks.executionLink}/${EXECUTION_TAB.BROADCAST}`, { replace: true });
		}
	}, [navigate, shouldShowVoiceCampaignsTab, tab]);

	const showSandboxButton =
		!configuredChannels.includes(CHANNELS.WHATSAPP) &&
		hasDialog360Provider &&
		isSandboxEnabled &&
		!isWhatsappSandboxConfigured;

	const tabOptions = [
		t(`pages:marketingCampaigns.execution.broadcast`),
		t(`pages:marketingCampaigns.execution.CTWA`),
		t(`pages:marketingCampaigns.execution.campaignAutomation`),
		t(`pages:voiceCampaign.analytics.tab`),
	];

	const broadcastTab = () => (
		<Tab title={tabOptions[0]} key={EXECUTION_TAB.BROADCAST}>
			{isWhatsAppCloudConfigured && (
				<div
					className={css({
						alignItems: 'center',
						backgroundColor: theme.colors.backgroundSecondary,
						border: `0.063rem solid ${theme.colors.primary}`,
						borderRadius: '0.5rem',
						display: 'flex',
						flexWrap: 'wrap',
						gap: '1rem',
						justifyContent: 'space-between',
						marginBottom: '1rem',
						padding: '1.25rem',
					})}
				>
					<div>
						<div
							className={css({ color: theme.colors.primary, fontWeight: 700, marginBottom: '0.375rem' })}
						>
							{t('pages:whatsappManager.numbers.labels.maxPricing.title')}
						</div>
						<div
							className={css({
								color: theme.colors.primaryA,
								fontSize: '0.875rem',
								lineHeight: '1.25rem',
							})}
						>
							{t(
								`pages:whatsappManager.numbers.labels.maxPricing.${
									isMaxPricingEnabled
										? 'broadcastEnabledDescription'
										: 'broadcastAgreementDescription'
								}`
							)}
						</div>
					</div>
					<MaxPricingEnrollment
						primary
						onStatusChange={setMaxPricingEnabled}
						onCreateTemplate={() =>
							navigate(`${ThreeColumnRouteLinks.createBroadcastMessageTemplates}?maxPricing=true`)
						}
					/>
				</div>
			)}
			<BroadcastHistory />
		</Tab>
	);

	const ctwaTab = () => (
		<Tab title={tabOptions[1]} key={EXECUTION_TAB.CTWA}>
			<AdDetailsTab />
		</Tab>
	);

	const campaignAutomationTab = () => (
		<Tab title={tabOptions[2]} key={EXECUTION_TAB.CAMPAIGN_AUTOMATION}>
			<CampaignsV2
				campaignModal={campaignModal}
				toggleCampaignModal={toggleCampaignModal}
				isEdit={isEdit}
				setIsEdit={setIsEdit}
				selectedCampaign={selectedCampaign}
				setSelectedCampaign={setSelectedCampaign}
			/>
		</Tab>
	);

	const voiceCampaignsTab = () => (
		<Tab title={tabOptions[3]} key={EXECUTION_TAB.VOICE_CAMPAIGNS}>
			<VoiceCampaigns />
		</Tab>
	);

	const tabElements = () => {
		if (isWAAcceleratorEnabled) {
			return [broadcastTab];
		}

		const tabs =
			isCTWAEntitlementEnabled &&
			marketingCampaignsAnalyticsType !== MARKETING_CAMPAIGNS_ENTITLEMENT_VALUES.PARTNER
				? [broadcastTab, ctwaTab, campaignAutomationTab]
				: [broadcastTab, campaignAutomationTab];

		return shouldShowVoiceCampaignsTab ? [...tabs, voiceCampaignsTab] : tabs;
	};

	const tabChangedHandler = (selectedTab: string) => {
		if (selectedTab !== tab) {
			navigate(`${ThreeColumnRouteLinks.executionLink}/${selectedTab}`);
		}
	};

	const createSandbox = () =>
		showSandboxButton ? (
			<ENGTButton
				onClick={() => {
					writeToLs(LOCAL_STORAGE_VALUES.SET_DIALOG360_ENABLED, 'true');
					writeToLs(LOCAL_STORAGE_VALUES.SET_SANDBOX_ENABLED, 'true');
					navigate(ThreeColumnRouteLinks.configureDepWhatsappLink);
				}}
				label={t('pages:landing.overview.accountNotConnected.connectToSandbox')}
				size={SIZE.compact}
				kind={KIND.primary}
				className={css({
					padding: '0.5rem',
					alignSelf: 'start',
				})}
			/>
		) : (
			<></>
		);

	const executionPageButtons = (
		<div className={css({ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' })}>
			{tab === EXECUTION_TAB.BROADCAST && (
				<>
					{createSandbox()}
					<CreateBroadcastBtn onClick={fetchAccountStatus} />
				</>
			)}
			{tab === EXECUTION_TAB.CTWA && <CreateClickToWAAdBtn />}
			{tab === EXECUTION_TAB.CAMPAIGN_AUTOMATION && !isAcceleratorEcom && (
				<CreateClampaignBtn
					onClick={() => {
						setSelectedCampaign({});
						toggleCampaignModal(true);
						setIsEdit(false);
					}}
				/>
			)}
			{tab === EXECUTION_TAB.VOICE_CAMPAIGNS &&
				!isAcceleratorEcom &&
				shouldShowVoiceCampaignsTab &&
				hasVoiceCampaignChannelConfigured && <CreateVoiceCampaignBtn />}
		</div>
	);

	const executionPageHeader = () => (
		<>
			<Block
				display='flex'
				justifyContent={['flex-end', 'flex-end', 'space-between']}
				alignItems='flex-end'
				flexWrap
				gridGap={['0rem', '0rem', '0rem', '2rem']}
			>
				<ENGTHeader>
					<PageHeader heading={t('pages:marketingCampaigns.execution.execution')} />
				</ENGTHeader>
				<Block display='flex' gridGap='1rem' marginRight={isSmallDesktopScreen ? '0.438rem' : '0rem'} flexWrap>
					{executionPageButtons}
				</Block>
			</Block>
			{isWhatsAppConfigured && (
				<PageSubHeading
					subHeading={renderWhatsappTemplateNote(isEngatiBrand, t)}
					cssOverrides={{
						marginBottom: '1rem',
					}}
				/>
			)}
		</>
	);

	return (
		<Layout>
			<LayoutHeader>{executionPageHeader()}</LayoutHeader>
			<LayoutContent>
				<ENGTTabs
					activeTab={tab}
					fill={FILL.intrinsic}
					onChange={tabChangedHandler}
					tabOverrides={tabsDefaultOverride({
						Tab: {
							style: {
								width: '25%',
								maxWidth: '100%',
							},
						},
						TabPanel: {
							style: {
								width: '100%',
							},
						},
						...(isSmallDesktopScreen && {
							TabList: {
								style: {
									marginBottom: '0.5rem',
								},
							},
						}),
					})}
				>
					{tabElements().map((renderTab: () => JSX.Element) => renderTab())}
				</ENGTTabs>
			</LayoutContent>
		</Layout>
	);
};

export default Execution;
